import React, { FC, useEffect, useCallback, useRef } from 'react';
import { useAppSelector } from 'store';
import { useDispatch } from 'react-redux';
import { OrganizationActions } from 'store/actions';
import { OrganizationExecutiveStatsItem } from './components';
import { MetadataTable } from '../OrganizationTreasury/components';
import { BlurredProBanner } from '../BlurredProBanner';
import { daoTreasuryInvestmentsPageIcon, peoplePageIcon } from 'assets';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { TooltipWrapper } from 'components/TooltipWrapper';
import { formatNumber } from './helpers';
import { OrganizationExecutiveSummaryProps } from './types';
import { financeTooltipText, governanceTooltipText, blurredContent } from './сonstants';

import styles from './styles.module.scss';

export const OrganizationExecutiveSummary: FC<OrganizationExecutiveSummaryProps> = ({ isPremiumAllowed }) => {
    const dispatch = useDispatch();
    const hasFetched = useRef(false);
    const { organizationData } = useAppSelector(({ organization }) => organization);
    const { executiveStats } = useAppSelector(({ organization }) => organization);

    const organizationId = organizationData?.id;
    const organizationName = organizationData?.name || 'Sample DAO';

    const fetchExecutiveStats = useCallback(() => {
        if (organizationId && !hasFetched.current && isPremiumAllowed) {
            hasFetched.current = true;
            dispatch(OrganizationActions.getExecutiveStats.request({ organizationId }));
        }
    }, [dispatch, organizationId, isPremiumAllowed]);

    useEffect(() => {
        fetchExecutiveStats();
    }, [fetchExecutiveStats]);

    if (!isPremiumAllowed) {
        return (
            <div className={styles.container}>
                <div className={styles.gridContainer}>
                    <BlurredProBanner
                        title="Financial metrics are a DeepDAO Pro feature"
                        description="Upgrade to Pro to access detailed financial analytics"
                    >
                        {blurredContent}
                    </BlurredProBanner>
                </div>
                <div className={styles.gridContainer}>
                    <BlurredProBanner
                        title="Governance metrics are a DeepDAO Pro feature"
                        description="Upgrade to Pro to access detailed governance analytics"
                    >
                        {blurredContent}
                    </BlurredProBanner>
                </div>
                <div className={styles.metadata}>
                    <div className={styles.data}>
                        <MetadataTable isBlurred={!isPremiumAllowed} />
                    </div>
                </div>
            </div>
        );
    }

    if (!executiveStats || Object.keys(executiveStats).length === 0) {
        return null;
    }

    return (
        <div className={styles.container}>
            <div>
                <>
                    <div className={styles.sectionTitle}>
                        <h4>Financial</h4>
                        <TooltipWrapper title={<span>{financeTooltipText}</span>}>
                            <InfoOutlinedIcon className={styles.infoIcon} />
                        </TooltipWrapper>
                    </div>

                    <div className={styles.gridContainer}>
                        <OrganizationExecutiveStatsItem
                            hasProgressBar={false}
                            hasProgressBarPercentage={true}
                            title="Token Price Gain"
                            icon={daoTreasuryInvestmentsPageIcon}
                            organizationName={organizationName}
                            circularPercentage={executiveStats.tokenPrice.orgRankInPercentage}
                            progressBarTitle={"deepDAO's Latest"}
                            progressBarBottomTitle={`${organizationName}'s token price gained`}
                            progressBarPercentage={executiveStats.tokenPrice.priceGain}
                            progressBarDescription={`over the past 3 months. Last update on DeepDAO at ${new Date(
                                executiveStats.tokenPrice.updatedAt,
                            )
                                .toLocaleDateString('en-US', {
                                    month: '2-digit',
                                    day: '2-digit',
                                    year: 'numeric',
                                })
                                .replace(/\//g, '/')}`}
                            description={`${organizationName}'s token price gained more than ${
                                executiveStats.tokenPrice.orgRank - 1
                            } DAOs (${executiveStats.tokenPrice.orgRankInPercentage}%) over the past 3 months.`}
                        />
                        <OrganizationExecutiveStatsItem
                            title="Treasury diversification"
                            icon={daoTreasuryInvestmentsPageIcon}
                            organizationName={organizationName}
                            circularPercentage={executiveStats.treasuryDiversification.orgRankInPercentage}
                            progressBarTitle={'current treasury'}
                            progressBarBottomTitle={`${organizationName} has`}
                            progressBarPercentage={executiveStats.treasuryDiversification.diversificationPercentage}
                            progressBarDescription={'of its treasury in tokens other than its own'}
                            description={`${organizationName} has diversified its treasury more than ${
                                executiveStats.treasuryDiversification.orgRank - 1
                            } DAOs (${executiveStats.treasuryDiversification.orgRankInPercentage}%).`}
                        />
                        <OrganizationExecutiveStatsItem
                            title="Token Reliance on Treasury"
                            icon={daoTreasuryInvestmentsPageIcon}
                            organizationName={organizationName}
                            circularPercentage={executiveStats.tokenReliance.orgRankInPercentage}
                            progressBarTitle={'current reliance'}
                            progressBarBottomTitle={`${organizationName} has`}
                            progressBarPercentage={executiveStats.tokenReliance.tokenReliancePercentage}
                            progressBarDescription={'of its token circulating supply in its treasury'}
                            description={`This ${organizationName} token has a higher percentage in its treasury, vs. its circulating supply, than ${
                                executiveStats.tokenReliance.orgRank - 1
                            } DAOs (${executiveStats.tokenReliance.orgRankInPercentage}%).`}
                        />
                    </div>

                    <div className={styles.sectionTitle}>
                        <h4>Governance</h4>
                        <TooltipWrapper title={<span>{governanceTooltipText}</span>}>
                            <InfoOutlinedIcon className={styles.infoIcon} />
                        </TooltipWrapper>
                    </div>

                    <div className={styles.gridContainer}>
                        <OrganizationExecutiveStatsItem
                            hasProgressBar={false}
                            hasProgressBarPercentage={false}
                            title="Active delegates"
                            icon={peoplePageIcon}
                            organizationName={organizationName}
                            circularPercentage={
                                executiveStats.activeDelegatesFor3Month.delegatesRankFor3MonthPercentage
                            }
                            progressBarTitle={'Current delegates'}
                            progressBarBottomTitle={`${organizationName} has`}
                            progressBarPercentage={executiveStats.activeDelegatesFor3Month.delegatesLast3Month || 0}
                            progressBarDescription={`active delegates with minimum 1 vote in the past 3 months`}
                            description={`${organizationName}'s has more active delegates than ${
                                executiveStats.activeDelegatesFor3Month.delegatesRankFor3Month - 1
                            } DAOs (${executiveStats.activeDelegatesFor3Month.delegatesRankFor3MonthPercentage}%) 
                            with Governor delegation.`}
                        />
                        <OrganizationExecutiveStatsItem
                            title="Dominant coalition"
                            icon={peoplePageIcon}
                            organizationName={organizationName}
                            circularPercentage={executiveStats.dominantCoalition.orgRankInPercentage}
                            progressBarTitle={'Current strongest coalition'}
                            progressBarBottomTitle={`${organizationName} has a coalition with`}
                            progressBarPercentage={executiveStats.dominantCoalition.votingPower3MonthWinningPercentage}
                            progressBarDescription={
                                'of the power required for winning a proposal during the past 3 months'
                            }
                            description={`${organizationName}'s strongest coalition is stronger than ${
                                executiveStats.dominantCoalition.orgRank - 1
                            } DAOs (${
                                executiveStats.dominantCoalition.orgRankInPercentage
                            }%) of the DAOs with DeepDAO Pro.`}
                        />
                        <OrganizationExecutiveStatsItem
                            hasProgressBar={false}
                            hasProgressBarPercentage={false}
                            title="Loyal voters"
                            icon={peoplePageIcon}
                            organizationName={organizationName}
                            circularPercentage={executiveStats.loyalVoters.loyalVotersRankPercentage}
                            progressBarTitle={'Current'}
                            progressBarBottomTitle={`${organizationName} has`}
                            progressBarPercentage={executiveStats.loyalVoters.numberOfLoyalVoters}
                            progressBarDescription={`loyal voters, who voted in each of the past 3 years. This is ${formatNumber(
                                executiveStats.loyalVoters.loyalVtsPercentagePerTokenHolders,
                            )} % of the DAO's token holders.`}
                            description={`${organizationName} has more loyal voters per token holder than ${
                                executiveStats.loyalVoters.loyalVotersRank - 1
                            } DAOs (${formatNumber(executiveStats.loyalVoters.loyalVotersRankPercentage)}%).`}
                        />
                        <OrganizationExecutiveStatsItem
                            hasProgressBar={false}
                            hasProgressBarPercentage={false}
                            title="Missing Voters"
                            icon={peoplePageIcon}
                            organizationName={organizationName}
                            circularPercentage={executiveStats.missingVoters.missingVotersRankPercentage}
                            progressBarTitle={'Current'}
                            progressBarBottomTitle={`${organizationName} has`}
                            progressBarPercentage={executiveStats.missingVoters.numberOfMissingVoters}
                            progressBarDescription={`missing voters, who voted previously in ${organizationName} but not in the past 12 months. This is ${formatNumber(
                                executiveStats.missingVoters.missingVtsPercentagePerTokenHolders,
                            )} % of the DAO's token holders.`}
                            description={`${organizationName} has more missing voters per token holder than ${
                                executiveStats.missingVoters.missingVotersRank - 1
                            } DAOs (${formatNumber(
                                executiveStats.missingVoters.missingVotersRankPercentage,
                            )}%) of the DAOs.`}
                        />
                    </div>
                </>
                <div className={styles.metadata}>
                    <div className={styles.data}>
                        <MetadataTable />
                    </div>
                </div>
            </div>
        </div>
    );
};
